/**
 * ERP-FIN-03 GL-JOURNAL-01: GL Journal Detail Page
 * Route: /gl/journals/:journalId
 * Cap: ledger.read (view), ledger.manage (lifecycle actions)
 */
import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { glApi, formatError } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { hasCapability } from "@/services/api";
import { toast } from "sonner";
import {
  BookOpen, ArrowLeft, Plus, Trash2, Edit3, CheckCircle,
  XCircle, Send, ShieldCheck, RotateCcw, Loader2,
  AlertCircle, Clock, FileText, Info, ChevronDown, ChevronUp,
  Eye, History,
} from "lucide-react";

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_CFG = {
  DRAFT:            { label: "Draft",           color: "text-[#706E6B]",  bg: "bg-[#F3F3F3]" },
  PENDING_APPROVAL: { label: "Pending Approval", color: "text-[#8C4B00]", bg: "bg-[#FFF4E5]" },
  APPROVED:         { label: "Approved",         color: "text-[#0176D3]", bg: "bg-[#E8F4FD]" },
  POSTED:           { label: "Posted",           color: "text-[#2E844A]", bg: "bg-[#EAF5EE]" },
  REVERSED:         { label: "Reversed",         color: "text-[#706E6B]", bg: "bg-[#F3F3F3]" },
  VOID:             { label: "Void",             color: "text-[#BA0517]", bg: "bg-[#FCEBEC]" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.DRAFT;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.color}`}>
      {cfg.label}
    </span>
  );
}

function fmt(minor) {
  if (minor == null || minor === 0) return "INR 0.00";
  return "INR " + (minor / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 });
}

// ── Add/Edit Line Modal ───────────────────────────────────────────────────────

function LineModal({ journal_id, lineToEdit, onClose, onSaved }) {
  const [form, setForm] = useState(lineToEdit ? {
    account_code:  lineToEdit.account_code,
    account_name:  lineToEdit.account_name || "",
    side:          lineToEdit.side,
    amount_minor:  lineToEdit.amount_minor,
    narration:     lineToEdit.narration || "",
  } : {
    account_code: "",
    account_name: "",
    side: "DEBIT",
    amount_minor: "",
    narration: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...form,
      amount_minor: parseInt(form.amount_minor, 10),
    };
    if (!payload.account_code.trim()) { toast.error("Account code is required"); return; }
    if (!payload.amount_minor || payload.amount_minor <= 0) { toast.error("Amount must be > 0"); return; }

    setSaving(true);
    try {
      if (lineToEdit) {
        await glApi.updateLine(journal_id, lineToEdit.id, payload);
        toast.success("Line updated");
      } else {
        await glApi.addLine(journal_id, payload);
        toast.success("Line added");
      }
      onSaved();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="line-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between">
          <h2 className="text-base font-bold text-[#1C1917]">{lineToEdit ? "Edit Line" : "Add Journal Line"}</h2>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#1C1917]"><XCircle size={18} /></button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-[#706E6B] mb-1">Account Code *</label>
              <input
                data-testid="line-account-code"
                className="w-full border border-[#E5E5E5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0176D3]"
                placeholder="e.g. 1020"
                value={form.account_code}
                onChange={e => setForm(f => ({ ...f, account_code: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#706E6B] mb-1">Account Name</label>
              <input
                data-testid="line-account-name"
                className="w-full border border-[#E5E5E5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0176D3]"
                placeholder="e.g. Bank / Cash Clearing"
                value={form.account_name}
                onChange={e => setForm(f => ({ ...f, account_name: e.target.value }))}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-[#706E6B] mb-1">Side *</label>
              <select
                data-testid="line-side"
                className="w-full border border-[#E5E5E5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0176D3]"
                value={form.side}
                onChange={e => setForm(f => ({ ...f, side: e.target.value }))}
              >
                <option value="DEBIT">DEBIT (Dr)</option>
                <option value="CREDIT">CREDIT (Cr)</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#706E6B] mb-1">Amount (paise) *</label>
              <input
                data-testid="line-amount"
                type="number"
                min="1"
                className="w-full border border-[#E5E5E5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0176D3]"
                placeholder="e.g. 100000000"
                value={form.amount_minor}
                onChange={e => setForm(f => ({ ...f, amount_minor: e.target.value }))}
                required
              />
              {form.amount_minor && parseInt(form.amount_minor) > 0 && (
                <p className="text-xs text-[#706E6B] mt-0.5">= {fmt(parseInt(form.amount_minor))}</p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#706E6B] mb-1">Line Narration</label>
            <input
              data-testid="line-narration"
              className="w-full border border-[#E5E5E5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0176D3]"
              placeholder="Optional description for this line"
              value={form.narration}
              onChange={e => setForm(f => ({ ...f, narration: e.target.value }))}
            />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 border border-[#E5E5E5] rounded-lg px-4 py-2 text-sm font-semibold text-[#706E6B] hover:bg-[#F3F3F3]">
              Cancel
            </button>
            <button type="submit" disabled={saving} data-testid="line-save-btn"
              className="flex-1 bg-[#1C1917] text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-[#3B3735] disabled:opacity-60 flex items-center justify-center gap-2">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
              {lineToEdit ? "Update Line" : "Add Line"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Confirm Action Dialog ─────────────────────────────────────────────────────

function ConfirmDialog({ title, description, fieldLabel, fieldRequired, fieldPlaceholder, onConfirm, onCancel, confirmLabel, confirmColor }) {
  const [value, setValue] = useState("");
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    if (fieldRequired && !value.trim()) { toast.error(`${fieldLabel} is required`); return; }
    setLoading(true);
    try { await onConfirm(value); }
    catch { /* handled by caller */ }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="px-6 py-4 border-b border-[#E5E5E5]">
          <h2 className="text-base font-bold text-[#1C1917]">{title}</h2>
        </div>
        <div className="px-6 py-4 space-y-4">
          <p className="text-sm text-[#706E6B]">{description}</p>
          {fieldLabel && (
            <div>
              <label className="block text-xs font-semibold text-[#706E6B] mb-1">
                {fieldLabel} {fieldRequired && <span className="text-[#BA0517]">*</span>}
              </label>
              <textarea
                data-testid="confirm-dialog-field"
                className="w-full border border-[#E5E5E5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0176D3] resize-none"
                rows={3}
                placeholder={fieldPlaceholder}
                value={value}
                onChange={e => setValue(e.target.value)}
              />
            </div>
          )}
        </div>
        <div className="px-6 pb-5 flex gap-3">
          <button onClick={onCancel}
            className="flex-1 border border-[#E5E5E5] rounded-lg px-4 py-2 text-sm font-semibold text-[#706E6B] hover:bg-[#F3F3F3]">
            Cancel
          </button>
          <button onClick={handleConfirm} disabled={loading}
            data-testid="confirm-action-btn"
            className={`flex-1 ${confirmColor || "bg-[#1C1917]"} text-white rounded-lg px-4 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-60 flex items-center justify-center gap-2`}>
            {loading && <Loader2 size={14} className="animate-spin" />}
            {confirmLabel || "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Detail Page ──────────────────────────────────────────────────────────

export default function GLJournalDetail() {
  const { journalId } = useParams();
  const navigate      = useNavigate();
  const { user }      = useAuth();
  const canManage     = hasCapability(user, "ledger.manage");

  const [journal,     setJournal]     = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [dialog,      setDialog]      = useState(null);   // { type, ... }
  const [lineModal,   setLineModal]   = useState(null);   // null | { lineToEdit }
  const [auditEvents, setAuditEvents] = useState(null);
  const [showAudit,   setShowAudit]   = useState(false);
  const [balanceChk,  setBalanceChk]  = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await glApi.getJournal(journalId);
      setJournal(res.data);
    } catch (err) {
      toast.error("Failed to load journal: " + formatError(err));
    } finally {
      setLoading(false);
    }
  }, [journalId]);

  useEffect(() => { load(); }, [load]);

  const loadAudit = async () => {
    try {
      const res = await glApi.getAudit(journalId);
      setAuditEvents(res.data.events || []);
      setShowAudit(true);
    } catch (err) {
      toast.error("Failed to load audit: " + formatError(err));
    }
  };

  const checkBalance = async () => {
    try {
      const res = await glApi.validateBalance(journalId);
      setBalanceChk(res.data);
    } catch (err) {
      toast.error("Failed to validate balance: " + formatError(err));
    }
  };

  const doAction = async (fn, successMsg) => {
    setActionLoading(successMsg);
    try {
      await fn();
      toast.success(successMsg);
      await load();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setActionLoading(null);
      setDialog(null);
    }
  };

  const deleteLine = async (lineId) => {
    try {
      await glApi.deleteLine(journalId, lineId);
      toast.success("Line removed");
      await load();
    } catch (err) {
      toast.error(formatError(err));
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen bg-[#F8F6F3]">
      <Loader2 size={28} className="animate-spin text-[#0176D3]" />
    </div>
  );

  if (!journal) return (
    <div className="flex items-center justify-center min-h-screen bg-[#F8F6F3]">
      <p className="text-[#706E6B]">Journal not found</p>
    </div>
  );

  const isDraft         = journal.status === "DRAFT";
  const isPending       = journal.status === "PENDING_APPROVAL";
  const isApproved      = journal.status === "APPROVED";
  const isPosted        = journal.status === "POSTED";
  const lines           = journal.lines || [];
  const drTotal         = journal.computed_dr ?? journal.total_debit_minor;
  const crTotal         = journal.computed_cr ?? journal.total_credit_minor;
  const isBalanced      = journal.computed_balanced ?? journal.is_balanced;
  const createdByMe     = journal.created_by === user?.id;

  return (
    <div className="min-h-screen bg-[#F8F6F3]" data-testid="gl-journal-detail">

      {/* Header */}
      <div className="bg-white border-b border-[#E5E5E5] px-8 py-5">
        <button onClick={() => navigate("/gl/journals")}
          className="flex items-center gap-1 text-sm text-[#706E6B] hover:text-[#1C1917] mb-4">
          <ArrowLeft size={14} /> Back to GL Journals
        </button>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#1C1917] rounded-lg">
              <BookOpen size={18} className="text-white" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-[#1C1917]" data-testid="journal-ref">
                  {journal.journal_ref}
                </h1>
                <StatusBadge status={journal.status} />
                {journal.journal_type === "REVERSAL" && (
                  <span className="text-xs bg-[#F3F3F3] text-[#706E6B] px-2 py-0.5 rounded-full font-semibold">
                    REVERSAL
                  </span>
                )}
              </div>
              <p className="text-xs text-[#706E6B] mt-0.5">
                {journal.accounting_date} · {journal.legal_entity_name || journal.legal_entity_id}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <button onClick={checkBalance}
              data-testid="btn-check-balance"
              className="flex items-center gap-1 border border-[#E5E5E5] rounded-lg px-3 py-1.5 text-xs font-semibold text-[#706E6B] hover:bg-[#F3F3F3]">
              <Eye size={12} /> Check Balance
            </button>
            <button onClick={loadAudit}
              data-testid="btn-show-audit"
              className="flex items-center gap-1 border border-[#E5E5E5] rounded-lg px-3 py-1.5 text-xs font-semibold text-[#706E6B] hover:bg-[#F3F3F3]">
              <History size={12} /> Audit Trail
            </button>

            {/* Lifecycle actions */}
            {canManage && isDraft && lines.length >= 2 && isBalanced && (
              <button
                data-testid="btn-submit-journal"
                onClick={() => setDialog({ type: "submit" })}
                className="flex items-center gap-1 bg-[#0176D3] text-white rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-[#014486]">
                <Send size={12} /> Submit for Approval
              </button>
            )}
            {canManage && isPending && !createdByMe && (
              <>
                <button
                  data-testid="btn-approve-journal"
                  onClick={() => setDialog({ type: "approve" })}
                  className="flex items-center gap-1 bg-[#2E844A] text-white rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-[#1E5631]">
                  <CheckCircle size={12} /> Approve
                </button>
                <button
                  data-testid="btn-reject-journal"
                  onClick={() => setDialog({ type: "reject" })}
                  className="flex items-center gap-1 border border-[#E5E5E5] rounded-lg px-3 py-1.5 text-xs font-semibold text-[#706E6B] hover:bg-[#F3F3F3]">
                  <XCircle size={12} /> Reject
                </button>
              </>
            )}
            {canManage && isPending && createdByMe && (
              <div className="flex items-center gap-1 text-xs text-[#8C4B00] bg-[#FFF4E5] px-3 py-1.5 rounded-lg">
                <AlertCircle size={12} /> SoD: A different user must approve
              </div>
            )}
            {canManage && isApproved && (
              <button
                data-testid="btn-post-journal"
                onClick={() => setDialog({ type: "post" })}
                className="flex items-center gap-1 bg-[#1C1917] text-white rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-[#3B3735]">
                <ShieldCheck size={12} /> Post Journal
              </button>
            )}
            {canManage && isPosted && !journal.reversal_journal && (
              <button
                data-testid="btn-reverse-journal"
                onClick={() => setDialog({ type: "reverse" })}
                className="flex items-center gap-1 border border-[#E5E5E5] rounded-lg px-3 py-1.5 text-xs font-semibold text-[#706E6B] hover:bg-[#F3F3F3]">
                <RotateCcw size={12} /> Reverse
              </button>
            )}
            {canManage && (isDraft || isPending) && (
              <button
                data-testid="btn-void-journal"
                onClick={() => setDialog({ type: "void" })}
                className="flex items-center gap-1 border border-[#BA0517] text-[#BA0517] rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-[#FCEBEC]">
                <XCircle size={12} /> Void
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="px-8 py-6 space-y-6">

        {/* Balance check result */}
        {balanceChk && (
          <div className={`rounded-xl p-4 border ${balanceChk.is_balanced ? "bg-[#EAF5EE] border-[#A3D4B4]" : "bg-[#FFF4E5] border-[#F0C060]"}`}
            data-testid="balance-check-result">
            <div className="flex items-center gap-2 text-sm font-semibold mb-2">
              {balanceChk.is_balanced ? <CheckCircle size={14} className="text-[#2E844A]" /> : <AlertCircle size={14} className="text-[#8C4B00]" />}
              {balanceChk.is_balanced ? "Journal is balanced ✓" : `Unbalanced — difference: ${fmt(balanceChk.difference)}`}
            </div>
            <div className="flex gap-6 text-xs">
              <span>Lines: {balanceChk.line_count}</span>
              <span>Total DR: {fmt(balanceChk.total_debit_minor)}</span>
              <span>Total CR: {fmt(balanceChk.total_credit_minor)}</span>
              <span className={balanceChk.ready_to_submit ? "text-[#2E844A] font-semibold" : "text-[#8C4B00]"}>
                {balanceChk.ready_to_submit ? "Ready to submit" : "Not ready to submit"}
              </span>
            </div>
          </div>
        )}

        {/* Narration + meta */}
        {journal.rejection_reason && (
          <div className="bg-[#FCEBEC] border border-[#F5A3A9] rounded-xl p-4" data-testid="rejection-reason">
            <p className="text-xs font-semibold text-[#BA0517] mb-1">Rejection Reason</p>
            <p className="text-sm text-[#1C1917]">{journal.rejection_reason}</p>
          </div>
        )}

        <div className="bg-white border border-[#E5E5E5] rounded-xl p-6">
          <h2 className="text-sm font-bold text-[#1C1917] mb-3">Journal Details</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-3 text-sm">
            <div>
              <p className="text-xs text-[#706E6B]">Narration</p>
              <p className="font-medium text-[#1C1917]">{journal.narration}</p>
            </div>
            <div>
              <p className="text-xs text-[#706E6B]">Legal Entity</p>
              <p className="font-medium">{journal.legal_entity_name || journal.legal_entity_id}</p>
            </div>
            <div>
              <p className="text-xs text-[#706E6B]">Ledger</p>
              <p className="font-medium">{journal.ledger_id}</p>
            </div>
            <div>
              <p className="text-xs text-[#706E6B]">Fiscal Period</p>
              <p className="font-medium">{journal.fiscal_period_name || journal.fiscal_period_id}</p>
            </div>
            <div>
              <p className="text-xs text-[#706E6B]">Currency</p>
              <p className="font-medium">{journal.currency_code}</p>
            </div>
            <div>
              <p className="text-xs text-[#706E6B]">Created By</p>
              <p className="font-medium text-xs">{journal.created_by_email}</p>
            </div>
            {journal.approved_by_email && (
              <div>
                <p className="text-xs text-[#706E6B]">Approved By</p>
                <p className="font-medium text-xs">{journal.approved_by_email}</p>
              </div>
            )}
            {journal.posted_by_email && (
              <div>
                <p className="text-xs text-[#706E6B]">Posted By</p>
                <p className="font-medium text-xs">{journal.posted_by_email}</p>
              </div>
            )}
          </div>

          {/* Balance summary */}
          <div className="flex items-center gap-6 mt-5 pt-4 border-t border-[#F3F3F3]">
            <div className="text-center">
              <p className="text-xs text-[#706E6B]">Total Debit</p>
              <p className="text-lg font-bold text-[#2E844A]">{fmt(drTotal)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-[#706E6B]">Total Credit</p>
              <p className="text-lg font-bold text-[#0176D3]">{fmt(crTotal)}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-[#706E6B]">Balanced</p>
              <div className={`mt-0.5 inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${
                isBalanced ? "bg-[#EAF5EE] text-[#2E844A]" : "bg-[#FFF4E5] text-[#8C4B00]"
              }`}>
                {isBalanced ? <CheckCircle size={11} /> : <AlertCircle size={11} />}
                {isBalanced ? "Yes" : "No"}
              </div>
            </div>
            <div className="text-center">
              <p className="text-xs text-[#706E6B]">Lines</p>
              <p className="text-lg font-bold text-[#1C1917]">{lines.length}</p>
            </div>
          </div>
        </div>

        {/* Lines table */}
        <div className="bg-white border border-[#E5E5E5] rounded-xl overflow-hidden" data-testid="journal-lines">
          <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E5E5]">
            <h2 className="text-sm font-bold text-[#1C1917]">Journal Lines</h2>
            {canManage && isDraft && (
              <button
                data-testid="btn-add-line"
                onClick={() => setLineModal({ lineToEdit: null })}
                className="flex items-center gap-1 bg-[#1C1917] text-white rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-[#3B3735]">
                <Plus size={12} /> Add Line
              </button>
            )}
          </div>

          {lines.length === 0 ? (
            <div className="px-5 py-8 text-center text-[#706E6B] text-sm" data-testid="lines-empty">
              No lines yet.{isDraft && canManage && " Click \"Add Line\" to add debit/credit entries."}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F8F6F3] border-b border-[#E5E5E5]">
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#706E6B]">#</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#706E6B]">Account</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#706E6B]">Side</th>
                  <th className="text-right px-4 py-2.5 text-xs font-semibold text-[#706E6B]">Amount</th>
                  <th className="text-left px-4 py-2.5 text-xs font-semibold text-[#706E6B]">Narration</th>
                  {canManage && isDraft && <th className="px-4 py-2.5"></th>}
                </tr>
              </thead>
              <tbody>
                {lines.map(line => (
                  <tr key={line.id} data-testid={`line-row-${line.id}`}
                    className="border-b border-[#F3F3F3] hover:bg-[#FAFAF9]">
                    <td className="px-4 py-2.5 text-xs text-[#706E6B]">{line.line_number}</td>
                    <td className="px-4 py-2.5">
                      <span className="font-mono font-semibold text-xs text-[#1C1917]">{line.account_code}</span>
                      {line.account_name && <span className="text-xs text-[#706E6B] ml-1.5">{line.account_name}</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        line.side === "DEBIT"
                          ? "bg-[#EAF5EE] text-[#2E844A]"
                          : "bg-[#E8F4FD] text-[#0176D3]"
                      }`}>
                        {line.side === "DEBIT" ? "Dr" : "Cr"}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold text-xs text-[#1C1917] whitespace-nowrap">
                      {fmt(line.amount_minor)}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-[#706E6B] max-w-[200px] truncate">
                      {line.narration || "—"}
                    </td>
                    {canManage && isDraft && (
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <button onClick={() => setLineModal({ lineToEdit: line })}
                            data-testid={`edit-line-${line.id}`}
                            className="text-[#706E6B] hover:text-[#0176D3]">
                            <Edit3 size={13} />
                          </button>
                          <button onClick={() => deleteLine(line.id)}
                            data-testid={`delete-line-${line.id}`}
                            className="text-[#706E6B] hover:text-[#BA0517]">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
                {/* Totals row */}
                <tr className="border-t-2 border-[#E5E5E5] bg-[#F8F6F3]">
                  <td colSpan={canManage && isDraft ? 3 : 3} className="px-4 py-2.5 text-xs font-bold text-[#1C1917] text-right">
                    TOTALS
                  </td>
                  <td className="px-4 py-2.5 text-right" colSpan={canManage && isDraft ? 2 : 2}>
                    <span className={`text-xs font-bold ${isBalanced ? "text-[#2E844A]" : "text-[#BA0517]"}`}>
                      Dr {fmt(drTotal)} / Cr {fmt(crTotal)}
                    </span>
                  </td>
                  {canManage && isDraft && <td />}
                </tr>
              </tbody>
            </table>
          )}
        </div>

        {/* Audit trail */}
        {showAudit && auditEvents && (
          <div className="bg-white border border-[#E5E5E5] rounded-xl p-5" data-testid="audit-trail-panel">
            <h2 className="text-sm font-bold text-[#1C1917] mb-3">Audit Trail</h2>
            {auditEvents.length === 0 ? (
              <p className="text-sm text-[#706E6B]">No audit events yet.</p>
            ) : (
              <div className="space-y-2">
                {auditEvents.map((ev, i) => (
                  <div key={i} className="flex items-start gap-3 text-xs">
                    <span className="text-[#706E6B] whitespace-nowrap pt-0.5 font-mono">
                      {(ev.timestamp || "").slice(0, 19).replace("T", " ")}
                    </span>
                    <span className="font-semibold text-[#1C1917] bg-[#F3F3F3] px-2 py-0.5 rounded whitespace-nowrap">
                      {ev.action}
                    </span>
                    <span className="text-[#706E6B]">{ev.summary}</span>
                    <span className="text-[#706E6B] ml-auto whitespace-nowrap">{ev.actor_email}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Line modal */}
      {lineModal && (
        <LineModal
          journal_id={journalId}
          lineToEdit={lineModal.lineToEdit}
          onClose={() => setLineModal(null)}
          onSaved={() => { setLineModal(null); load(); }}
        />
      )}

      {/* Action dialogs */}
      {dialog?.type === "submit" && (
        <ConfirmDialog
          title="Submit for Approval"
          description={`Submit journal ${journal.journal_ref} for maker-checker approval? A different user must approve.`}
          fieldLabel="Submission notes (optional)"
          fieldRequired={false}
          fieldPlaceholder="Optional notes for the approver..."
          confirmLabel="Submit"
          confirmColor="bg-[#0176D3]"
          onCancel={() => setDialog(null)}
          onConfirm={async (notes) => {
            await doAction(
              () => glApi.submit(journalId, { submission_notes: notes || null }),
              "Journal submitted for approval"
            );
          }}
        />
      )}
      {dialog?.type === "approve" && (
        <ConfirmDialog
          title="Approve Journal"
          description={`Approve journal ${journal.journal_ref}? You are the checker — SoD is satisfied.`}
          fieldLabel="Approval notes (optional)"
          fieldRequired={false}
          fieldPlaceholder="Optional approval notes..."
          confirmLabel="Approve"
          confirmColor="bg-[#2E844A]"
          onCancel={() => setDialog(null)}
          onConfirm={async (notes) => {
            await doAction(
              () => glApi.approve(journalId, { approval_notes: notes || null }),
              "Journal approved"
            );
          }}
        />
      )}
      {dialog?.type === "reject" && (
        <ConfirmDialog
          title="Reject Journal"
          description="Rejecting returns the journal to DRAFT for correction."
          fieldLabel="Rejection reason"
          fieldRequired={true}
          fieldPlaceholder="Explain why this journal needs correction..."
          confirmLabel="Reject"
          confirmColor="bg-[#BA0517]"
          onCancel={() => setDialog(null)}
          onConfirm={async (reason) => {
            await doAction(
              () => glApi.reject(journalId, { rejection_reason: reason }),
              "Journal rejected — returned to maker"
            );
          }}
        />
      )}
      {dialog?.type === "post" && (
        <ConfirmDialog
          title="Post Journal"
          description={`Post journal ${journal.journal_ref} permanently? This action creates an immutable accounting entry. The period must be OPEN.`}
          fieldLabel="Posting notes (optional)"
          fieldRequired={false}
          fieldPlaceholder="Optional posting notes..."
          confirmLabel="Post Journal"
          confirmColor="bg-[#1C1917]"
          onCancel={() => setDialog(null)}
          onConfirm={async (notes) => {
            await doAction(
              () => glApi.post(journalId, { posting_notes: notes || null }),
              "Journal posted successfully"
            );
          }}
        />
      )}
      {dialog?.type === "void" && (
        <ConfirmDialog
          title="Void Journal"
          description="Void this journal? It will be permanently cancelled (cannot be posted). POSTED journals must be reversed instead."
          fieldLabel="Void reason"
          fieldRequired={true}
          fieldPlaceholder="Reason for voiding this journal..."
          confirmLabel="Void Journal"
          confirmColor="bg-[#BA0517]"
          onCancel={() => setDialog(null)}
          onConfirm={async (reason) => {
            await doAction(
              () => glApi.voidJournal(journalId, { void_reason: reason }),
              "Journal voided"
            );
          }}
        />
      )}
      {dialog?.type === "reverse" && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
            <div className="px-6 py-4 border-b border-[#E5E5E5]">
              <h2 className="text-base font-bold text-[#1C1917]">Reverse Journal</h2>
            </div>
            <ReverseForm
              journalRef={journal.journal_ref}
              onCancel={() => setDialog(null)}
              onConfirm={async ({ reason, date }) => {
                await doAction(
                  () => glApi.reverse(journalId, { reversal_reason: reason, reversal_date: date }),
                  "Journal reversed — compensating entry created"
                );
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function ReverseForm({ journalRef, onCancel, onConfirm }) {
  const [reason, setReason] = useState("");
  const [date,   setDate]   = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!reason.trim() || reason.length < 10) { toast.error("Reversal reason must be at least 10 characters"); return; }
    setLoading(true);
    try { await onConfirm({ reason, date }); }
    catch { /* handled upstream */ }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
      <p className="text-sm text-[#706E6B]">
        Create a compensating reversal journal for <strong>{journalRef}</strong>. 
        All DR/CR sides will be inverted and the reversal will be auto-posted.
      </p>
      <div>
        <label className="block text-xs font-semibold text-[#706E6B] mb-1">Reversal Date *</label>
        <input type="date" required
          data-testid="reverse-date"
          className="w-full border border-[#E5E5E5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0176D3]"
          value={date} onChange={e => setDate(e.target.value)} />
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#706E6B] mb-1">Reversal Reason * (min 10 chars)</label>
        <textarea required minLength={10}
          data-testid="reverse-reason"
          className="w-full border border-[#E5E5E5] rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0176D3] resize-none"
          rows={3} placeholder="Explain why this journal is being reversed..."
          value={reason} onChange={e => setReason(e.target.value)} />
      </div>
      <div className="flex gap-3 pt-1">
        <button type="button" onClick={onCancel}
          className="flex-1 border border-[#E5E5E5] rounded-lg px-4 py-2 text-sm font-semibold text-[#706E6B] hover:bg-[#F3F3F3]">Cancel</button>
        <button type="submit" disabled={loading}
          data-testid="reverse-submit-btn"
          className="flex-1 bg-[#1C1917] text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-[#3B3735] disabled:opacity-60 flex items-center justify-center gap-2">
          {loading && <Loader2 size={14} className="animate-spin" />}
          Create Reversal
        </button>
      </div>
    </form>
  );
}
